import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import ReCAPTCHA from 'react-google-recaptcha';
import useTranslation from '../../customHooks/translations';
import agent from '../../agent';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';

const CONTEXTS = [
  { value: 'wof', labelKey: 'contextWof' },
  { value: 'bike', labelKey: 'contextBike' },
  { value: 'fishing', labelKey: 'contextFishing' },
  { value: 'trip', labelKey: 'contextTrip' },
  { value: 'fit', labelKey: 'contextFit' },
  { value: 'running', labelKey: 'contextRunning' },
  { value: 'dogs', labelKey: 'contextDogs' },
  { value: 'games', labelKey: 'contextGames' },
  { value: 'books', labelKey: 'contextBooks' },
  { value: 'food', labelKey: 'contextFood' },
  { value: 'shopping', labelKey: 'contextShopping' },
  { value: 'tech', labelKey: 'contextTech' },
  { value: 'motorization', labelKey: 'contextMotorization' },
  { value: 'gardening', labelKey: 'contextGardening' },
  { value: 'music', labelKey: 'contextMusic' },
  { value: 'sport', labelKey: 'contextSport' },
  { value: 'winterSports', labelKey: 'contextWinterSports' },
  { value: 'horseRiding', labelKey: 'contextHorseRiding' },
  { value: 'movies', labelKey: 'contextMovies' },
  { value: 'series', labelKey: 'contextSeries' },
  { value: 'skateboard', labelKey: 'contextSkateboard' },
  { value: 'inlineSkate', labelKey: 'contextInlineSkate' },
  { value: 'scooter', labelKey: 'contextScooter' },
  { value: 'bmx', labelKey: 'contextBmx' },
  { value: 'mushroom', labelKey: 'contextMushroom' },
  { value: 'nordicWalking', labelKey: 'contextNordicWalking' },
];

const MAX_MESSAGE_LENGTH = 1000;

const FeedbackView = () => {
  const translation = useTranslation();
  const recaptchaRef = React.useRef(null);
  const [context, setContext] = useState('overall');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [captchaError, setCaptchaError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

  const handleSubmit = async () => {
    if (!message.trim()) return;

    const token = recaptchaRef.current.getValue();
    if (!token) {
      setCaptchaError(true);
      return;
    }
    setCaptchaError(false);

    setSubmitting(true);
    try {
      await agent.Feedback.sendContact({
        context,
        message: message.trim(),
        token,
        ...(email.trim() ? { email: email.trim() } : {}),
      });
      setMessage('');
      setEmail('');
      setContext('overall');
      recaptchaRef.current.reset();
      setSnackbar({ open: true, severity: 'success', message: translation.feedback.successMessage });
    } catch (error) {
      setSnackbar({ open: true, severity: 'error', message: translation.feedback.errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" px={2} py={{ xs: 4, sm: 8 }}>
      <Grid container spacing={2} sx={{ maxWidth: 480, width: '100%' }}>
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{translation.feedback.title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{translation.feedback.subtitle}</Typography>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel id="feedback-context-label">{translation.feedback.contextLabel}</InputLabel>
            <Select
              labelId="feedback-context-label"
              label={translation.feedback.contextLabel}
              value={context}
              onChange={e => setContext(e.target.value)}
            >
              <MenuItem value="overall">{translation.feedback.contextOverall}</MenuItem>
              {CONTEXTS.map(({ value, labelKey }) => (
                <MenuItem key={value} value={value}>{translation.settings[labelKey]}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={translation.feedback.messageLabel}
            multiline
            rows={5}
            fullWidth
            value={message}
            onChange={e => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
            helperText={`${message.length}/${MAX_MESSAGE_LENGTH}`}
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={translation.feedback.emailLabel}
            type="email"
            fullWidth
            value={email}
            onChange={e => setEmail(e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECHAPTA_SITE_KEY}
          />
          {captchaError && (
            <Typography variant="caption" sx={{ color: '#f44336' }}>{translation.feedback.captchaError}</Typography>
          )}
        </Grid>
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!message.trim() || submitting}
            sx={{
              backgroundColor: DEFAULT_THEME_COLOR,
              color: 'white',
              borderRadius: 4,
              px: 4,
              '&:hover': { backgroundColor: DEFAULT_THEME_COLOR, opacity: 0.85 },
            }}
          >
            {submitting ? '…' : translation.feedback.submit}
          </Button>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeedbackView;
